import React from 'react'
import {
  ArrowLeft, Undo2, Redo2,
  Grid3X3, Eye, Save,
  Minus, Plus,
  Settings, Upload, Command,
} from 'lucide-react'
import type { CmsEditorHeaderProps } from './types'

/* ── Device modes ────────────────────────────────────── */

const DEVICE_MODES = [
  { key: 'desktop' as const, label: 'DESKTOP' },
  { key: 'tablet' as const, label: 'TABLET' },
  { key: 'mobile' as const, label: 'MOBILE' },
]

/* ── Header Component ────────────────────────────────── */

function CmsEditorHeaderInner({
  slug,
  isDirty,
  isPending,
  deviceMode,
  canvasZoom,
  showGrid,
  isPreviewMode,
  canUndo,
  canRedo,
  onNavigateBack,
  onSetDeviceMode,
  onToggleGrid,
  onZoomIn,
  onZoomOut,
  onUndo,
  onRedo,
  onTogglePreview,
  onSave,
  onOpenCommandPalette,
  onOpenPageSettings,
  onOpenExportImport,
}: CmsEditorHeaderProps) {
  return (
    <header className="editor-header">
      {/* ── Left zone: Back + Breadcrumb ─────────────── */}
      <div className="editor-header-zone editor-header-left">
        <button
          className="editor-btn editor-btn-ghost editor-btn-icon"
          onClick={onNavigateBack}
          aria-label="Back to pages"
        >
          <ArrowLeft size={14} aria-hidden="true" />
        </button>

        <nav className="editor-breadcrumb">
          <span>Pages</span>
          <span className="editor-breadcrumb-sep">›</span>
          <span className="editor-breadcrumb-current">{slug || 'Untitled'}</span>
        </nav>
      </div>

      {/* ── Center zone: Undo / Redo ─────────────────── */}
      <div className="editor-header-zone editor-header-center">
        <button
          className="editor-btn editor-btn-ghost editor-btn-icon"
          onClick={onUndo}
          disabled={!canUndo}
          aria-label="Undo (⌘Z)"
        >
          <Undo2 size={14} aria-hidden="true" />
        </button>
        <button
          className="editor-btn editor-btn-ghost editor-btn-icon"
          onClick={onRedo}
          disabled={!canRedo}
          aria-label="Redo (⌘⇧Z)"
        >
          <Redo2 size={14} aria-hidden="true" />
        </button>
      </div>

      {/* ── Right zone: Devices, Zoom, Actions ───────── */}
      <div className="editor-header-zone editor-header-right">
        {/* Device mode toggles */}
        <div style={{ display: 'flex', gap: '2px' }}>
          {DEVICE_MODES.map(({ key, label }) => (
            <button
              key={key}
              className="editor-device-btn"
              data-active={deviceMode === key}
              onClick={() => onSetDeviceMode(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Separator */}
        <div style={{ width: 1, height: 16, background: 'var(--editor-border-dim)', margin: '0 4px' }} />

        {/* Zoom controls */}
        <div className="editor-zoom">
          <button
            onClick={onZoomOut}
            aria-label="Zoom out"
            className="editor-btn editor-btn-ghost editor-btn-icon editor-btn-sm"
          >
            <Minus size={12} aria-hidden="true" />
          </button>
          <span style={{ minWidth: 36, textAlign: 'center' }}>
            {Math.round(canvasZoom * 100)}%
          </span>
          <button
            className="editor-btn editor-btn-ghost editor-btn-icon editor-btn-sm"
            onClick={onZoomIn}
            aria-label="Zoom in"
          >
            <Plus size={12} aria-hidden="true" />
          </button>
        </div>

        {/* Separator */}
        <div style={{ width: 1, height: 16, background: 'var(--editor-border-dim)', margin: '0 4px' }} />

        {/* Grid toggle */}
        <button
          className={`editor-btn editor-btn-ghost editor-btn-icon ${showGrid ? '' : ''}`}
          onClick={onToggleGrid}
          aria-label="Toggle grid"
          aria-pressed={showGrid}
          style={{ color: showGrid ? 'var(--editor-accent)' : undefined }}
        >
          <Grid3X3 size={14} aria-hidden="true" />
        </button>

        {/* Preview toggle */}
        <button
          className="editor-btn editor-btn-ghost editor-btn-icon"
          onClick={onTogglePreview}
          aria-label="Preview mode"
          aria-pressed={isPreviewMode}
          style={{ color: isPreviewMode ? 'var(--editor-accent)' : undefined }}
        >
          <Eye size={14} aria-hidden="true" />
        </button>

        {/* Page settings */}
        <button
          className="editor-btn editor-btn-ghost editor-btn-icon"
          onClick={onOpenPageSettings}
          aria-label="Page settings"
        >
          <Settings size={14} aria-hidden="true" />
        </button>

        {/* Export */}
        <button
          className="editor-btn editor-btn-ghost editor-btn-icon"
          onClick={onOpenExportImport}
          aria-label="Export / Import"
        >
          <Upload size={12} aria-hidden="true" />
        </button>

        {/* Command palette trigger */}
        <button
          className="editor-btn editor-btn-ghost editor-btn-sm"
          onClick={onOpenCommandPalette}
          aria-label="Command palette (⌘K)"
          style={{ gap: 4 }}
        >
          <Command size={12} aria-hidden="true" />
          <span className="editor-kbd" style={{ marginLeft: 2 }}>⌘K</span>
        </button>

        {/* Separator */}
        <div style={{ width: 1, height: 16, background: 'var(--editor-border-dim)', margin: '0 4px' }} />

        {/* Publish / Save */}
        <button
          className="editor-btn editor-btn-primary editor-btn-sm"
          onClick={onSave}
          disabled={isPending}
          style={{ position: 'relative' }}
        >
          <Save size={12} />
          <span>{isPending ? 'Saving…' : 'Publish'}</span>
          {isDirty && !isPending && (
            <span className="editor-dirty-dot" style={{ position: 'absolute', top: -2, right: -2 }} />
          )}
        </button>
      </div>
    </header>
  )
}

const CmsEditorHeader = React.memo(CmsEditorHeaderInner)
export default CmsEditorHeader
